import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, MONTHS } from './_finHelpers';

// Amortization Calculator — matches image with optional extra payments
export default function AmortizationCalc() {
  const [amount, setAmount] = useState(200000);
  const [years, setYears] = useState(15);
  const [months, setMonths] = useState(0);
  const [rate, setRate] = useState(6);
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [showExtras, setShowExtras] = useState(false);
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [extraMonthlyStartM, setExtraMonthlyStartM] = useState(new Date().getMonth() + 1);
  const [extraMonthlyStartY, setExtraMonthlyStartY] = useState(new Date().getFullYear());
  const [extraYearly, setExtraYearly] = useState(0);
  const [extraYearlyStartM, setExtraYearlyStartM] = useState(new Date().getMonth() + 1);
  const [extraYearlyStartY, setExtraYearlyStartY] = useState(new Date().getFullYear());
  const [oneTimes, setOneTimes] = useState([
    { amount: 0, month: new Date().getMonth() + 1, year: new Date().getFullYear() }
  ]);

  const addOneTime = () => setOneTimes(prev => [...prev, { amount: 0, month: new Date().getMonth()+1, year: new Date().getFullYear() }]);
  const removeOneTime = (i) => setOneTimes(prev => prev.filter((_,j) => j !== i));
  const updateOneTime = (i, patch) => setOneTimes(prev => prev.map((x,j) => j===i ? { ...x, ...patch } : x));

  const c = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12 + months;
    const basePmt = r === 0 ? amount / n : (amount * r * Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    let bal = amount;
    const monthly = []; const yearly = [];
    let yInt=0, yPrin=0, yPay=0;
    let cm = startMonth, cy = startYear;
    let totalPaid = 0, totalInterest = 0;
    let m = 0;
    while (bal > 0.01 && m < 1200) {
      m++;
      const interest = bal * r;
      let princPay = basePmt - interest;
      let extra = 0;
      if (showExtras) {
        // Monthly extra after start date
        if ((cy > extraMonthlyStartY) || (cy === extraMonthlyStartY && cm >= extraMonthlyStartM)) extra += extraMonthly;
        // Yearly extra on/after start (once per year)
        if ((cy > extraYearlyStartY) || (cy === extraYearlyStartY && cm >= extraYearlyStartM)) {
          if (cm === extraYearlyStartM) extra += extraYearly;
        }
        // One-time (multiple supported)
        for (const ot of oneTimes) {
          if (cy === ot.year && cm === ot.month) extra += ot.amount;
        }
      }
      let pmt = basePmt + extra;
      if (princPay + extra > bal) { princPay = bal; pmt = interest + bal; extra = pmt - basePmt; }
      bal = Math.max(0, bal - princPay - extra);
      totalPaid += pmt; totalInterest += interest;
      monthly.push({ month: `${MONTHS[cm-1]} ${cy}`, interest, principal: princPay + extra, balance: bal, payment: pmt });
      yInt += interest; yPrin += (princPay + extra); yPay += pmt;
      if (cm === 12 || bal <= 0.01) {
        yearly.push({ year: yearly.length + 1, interest: yInt, principal: yPrin, balance: bal, payment: yPay });
        yInt=0; yPrin=0; yPay=0;
      }
      cm++; if (cm > 12) { cm = 1; cy++; }
    }
    return { basePmt, monthly, yearly, totalPaid, totalInterest, months: m };
  }, [amount, years, months, rate, startMonth, startYear, showExtras, extraMonthly, extraMonthlyStartM, extraMonthlyStartY, extraYearly, extraYearlyStartM, extraYearlyStartY, oneTimes]);

  return (
    <>
      <CalcShell look="cnet" title="Amortization Calculator" subtitle="Full month-by-month or year-by-year breakdown of principal, interest, and remaining balance."
        about={<>
          <p>An <b>amortization schedule</b> shows how each periodic payment is split between principal and interest, and how the remaining loan balance decreases over time.</p>
          <p>Making <b>extra payments</b> reduces the principal faster, which lowers total interest and shortens the loan term — often significantly.</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Loan Term</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
                <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
              </div>
            </div>
            <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
            <div className="field"><label>Loan start date</label>
              <div className="grid grid-cols-2 gap-2">
                <select value={startMonth} onChange={e=>setStartMonth(+e.target.value)}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
                <input type="number" value={startYear} onChange={e=>setStartYear(+e.target.value||0)}/>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mt-3 font-semibold"><input type="checkbox" checked={showExtras} onChange={e=>setShowExtras(e.target.checked)} data-testid="amort-extras"/> Optional: make extra payments</label>
            {showExtras && <>
              <div className="field mt-3"><label>Extra monthly pay</label>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={extraMonthly} onChange={e=>setExtraMonthly(+e.target.value||0)} className="flex-1"/></div>
                  <select value={extraMonthlyStartM} onChange={e=>setExtraMonthlyStartM(+e.target.value)}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
                  <input type="number" value={extraMonthlyStartY} onChange={e=>setExtraMonthlyStartY(+e.target.value||0)}/>
                </div>
              </div>
              <div className="field"><label>Extra yearly pay</label>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={extraYearly} onChange={e=>setExtraYearly(+e.target.value||0)} className="flex-1"/></div>
                  <select value={extraYearlyStartM} onChange={e=>setExtraYearlyStartM(+e.target.value)}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
                  <input type="number" value={extraYearlyStartY} onChange={e=>setExtraYearlyStartY(+e.target.value||0)}/>
                </div>
              </div>
              <div className="field">
                <label>Extra one-time pay</label>
                {oneTimes.map((ot, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 mb-2">
                    <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={ot.amount} onChange={e=>updateOneTime(i,{amount:+e.target.value||0})} className="flex-1"/></div>
                    <select value={ot.month} onChange={e=>updateOneTime(i,{month:+e.target.value})}>{MONTHS.map((m,j)=><option key={m} value={j+1}>{m}</option>)}</select>
                    <input type="number" value={ot.year} onChange={e=>updateOneTime(i,{year:+e.target.value||0})} className="w-24"/>
                    {oneTimes.length > 1 && <button onClick={()=>removeOneTime(i)} className="text-red-600 hover:text-red-800 text-xs px-1" title="Remove">✕</button>}
                    {oneTimes.length === 1 && <span/>}
                  </div>
                ))}
                <button onClick={addOneTime} data-testid="amort-add-onetime"
                  className="text-blue-700 hover:text-blue-900 text-sm font-semibold mt-1">+ add more one-time payment</button>
              </div>
            </>}
          </div>
          <div>
            <div className="result-panel mb-4"><div className="result-title">Monthly Pay</div><div className="result-value" data-testid="amort-monthly">{fmt(c.basePmt)}</div></div>
            <table className="data-table"><tbody>
              <tr><td>Total of {c.months} monthly payments</td><td className="text-right font-semibold">{fmt(c.totalPaid)}</td></tr>
              <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
            </tbody></table>
            <div className="mt-4"><PieChart parts={[{label:'Principal',value:amount,color:'#2563eb'},{label:'Interest',value:c.totalInterest,color:'#10b981'}]} size={180}/></div>
          </div>
        </div>
        <StepsPanel steps={[
          `Monthly rate r = ${rate}%/12 = ${(rate/12).toFixed(4)}%`,
          `Base payment M = <b>${fmt(c.basePmt)}</b> for ${years*12+months} months`,
          showExtras ? `Extra payments applied — loan paid off in <b>${c.months}</b> months` : `Loan paid off in ${c.months} months`,
        ]}/>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.yearly} startBalance={amount}/></div>
        </div>
      </CalcShell>
    </>
  );
}
